import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Input from 'react-phone-number-input/input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { FaPlus, FaTrash, FaSave, FaEnvelope, FaUser, FaPhone } from 'react-icons/fa';

import 'react-phone-number-input/style.css';
import axios from 'axios';
import './UpdateQR.css';


// Add custom styles for the phone input
const phoneInputStyle = {
  width: '100%',
  height: 'calc(1.5em + 0.75rem + 2px)',
  padding: '0.375rem 0.75rem 0.375rem 2.5rem',
  fontSize: '1rem',
  fontWeight: '400',
  lineHeight: '1.5',
  color: '#495057',
  backgroundColor: '#fff',
  backgroundClip: 'padding-box',
  border: '1px solid #e0e0e0',
  borderRadius: '0.5rem',
  boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease'
};

const UpdateQR = () => {
  const { pathname } = useLocation(); // e.g. "/gabcdef123"
  const randStrUpd = pathname.replace('/u', ''); // "abcdef123"
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const [contacts, setContacts] = useState([]);
  const [phoneErrors, setPhoneErrors] = useState([]);
  const [originalData, setOriginalData] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchQRInfo = async () => {
      try {
        // First, get the data using the public endpoint
        // This is a workaround since we don't have a dedicated endpoint to get data by private keys
        const response = await axios.get(`/api/v1/up/${randStrUpd}`);
        
        setEmail(response.data.email);
        setComment(response.data.comment || '');
        const initialContacts = response.data.contacts.length > 0 
          ? response.data.contacts 
          : [{ name: '', phone: '' }];
        setContacts(initialContacts);
        setPhoneErrors(new Array(initialContacts.length).fill(''));
        setOriginalData(response.data);
        setIsLoading(false);
      } catch (error) {
        setError(error.response?.data?.detail || 'Не удалось загрузить информацию QR-карты');
        setIsLoading(false);
      }
    };

    fetchQRInfo();
  }, [randStrUpd]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[index][field] = value;
    setContacts(updatedContacts);
    
    // Validate phone number if the field is 'phone'
    if (field === 'phone') {
      const updatedErrors = [...phoneErrors];
      if (value) {
        updatedErrors[index] = isPossiblePhoneNumber(value) ? '' : 'Неверный формат номера телефона';
      } else {
        updatedErrors[index] = 'Номер телефона обязателен';
      }
      setPhoneErrors(updatedErrors);
    }
  };
  
  const validateAllPhones = () => {
    // If there are no contacts, validation passes
    if (contacts.length === 0) {
      return true;
    }
    
    const newErrors = contacts.map(contact => {
      if (!contact.phone) {
        return 'Номер телефона обязателен';
      }
      return isPossiblePhoneNumber(contact.phone) ? '' : 'Неверный формат номера телефона';
    });
    
    setPhoneErrors(newErrors);
    return newErrors.every(error => error === '');
  };

  const addContact = () => {
    setContacts([...contacts, { name: '', phone: '' }]);
    setPhoneErrors([...phoneErrors, '']);
  };

  const removeContact = (index) => {
    const updatedContacts = [...contacts];
    updatedContacts.splice(index, 1);
    setContacts(updatedContacts);
    
    const updatedErrors = [...phoneErrors];
    updatedErrors.splice(index, 1);
    setPhoneErrors(updatedErrors);
    // Note: This only updates the UI. Changes will be saved to the backend
    // when the form is submitted via the 'Save Changes' button
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all phone numbers before submission
    const isValid = validateAllPhones();
    if (!isValid) {
      return;
    }
    
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await axios.put(`/api/v1/up/${randStrUpd}`, {
        email,
        comment,
        contacts
      });

      setSuccess(true);
      setIsSaving(false);
    } catch (error) {
      setError(error.response?.data?.detail || 'Произошла ошибка при обновлении QR-карты');
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="update-qr container text-center">
        <div className="loading-container">
          <h2>Загрузка информации...</h2>
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (error && !originalData) {
    return (
      <div className="update-qr container">
        <div className="card error-card">
          <h2 className="text-center">Ошибка</h2>
          <div className="alert alert-danger">{error}</div>
        </div>
      </div>
    );
  }

  return (
  <div>
    <div className="update-qr container">
      <div className="qr-card">
        <h2 className="card-title">Обновить QR-карту</h2>
        
        {success && (
          <div className="alert alert-success">
            <span>✓</span> QR-карта успешно обновлена!
          </div>
        )}
        
        {error && (
          <div className="alert alert-danger">
            <span>⚠</span> {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <span className="input-icon"><FaEnvelope /></span>
              <input
                type="email"
                id="email"
                className="form-control"
                value={email}
                onChange={handleEmailChange}
                placeholder="Ваш email адрес"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="comment">Комментарий</label>
            <textarea
              id="comment"
              className="form-control"
              value={comment}
              onChange={handleCommentChange}
              placeholder="Ваше ФИО, группа крови или другая доп информация."
              rows="3"
            />
          </div>

          {contacts.length > 0 && (
            <>
              <div className="section-header">
                <h3>Контакты</h3>
              </div>
              
              <div className="contacts-container">
                {contacts.map((contact, index) => (
              <div key={index} className="contact-card">
                <div className="contact-header">
                  <span className="contact-number">Контакт {index + 1}</span>
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => removeContact(index)}
                    aria-label="Удалить контакт"
                  >
                    <FaTrash />
                  </button>
                </div>
                
                <div className="form-group">
                  <label htmlFor={`name-${index}`}>Имя</label>
                  <div className="input-wrapper">
                    <span className="input-icon"><FaUser /></span>
                    <input
                      type="text"
                      id={`name-${index}`}
                      className="form-control"
                      value={contact.name}
                      onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                      placeholder="Введите имя"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor={`phone-${index}`}>Телефон</label>
                  <div className="input-wrapper phone-input-wrapper">
                    <span className="input-icon"><FaPhone /></span>
                    <Input
                      defaultCountry="RU"
                      id={`phone-${index}`}
                      className="form-control"
                      placeholder="Введите номер телефона"
                      value={contact.phone && contact.phone.startsWith('+') ? contact.phone : ''}
                      onChange={(value) => handleContactChange(index, 'phone', value || '')}
                      required
                    />
                  </div>
                  {phoneErrors[index] && (
                    <div className="error-message">
                      {phoneErrors[index]}
                    </div>
                  )}
                </div>
              </div>
                ))}
              </div>
            </>
          )}

          <div className="add-contact-wrapper">
            <button
              type="button"
              className="add-contact-btn"
              onClick={addContact}
            >
              <FaPlus /> Добавить контакт
            </button>
          </div>

          <div className="submit-wrapper">
            <button
              type="submit"
              className="submit-btn"
              disabled={isSaving}
            >
              {isSaving ? 'Сохранение...' : <><FaSave /> Сохранить изменения</>}
            </button>
          </div>
        </form>
      </div>
    </div>
    <div className="qr-add">
      <span className="qr-add-text">{originalData.id + 5555}</span>
    </div>
  </div>
  );
};

export default UpdateQR;
